import { FC } from "react";
import { NumericInput } from "@landtechnologies/components";
import debounce from "lodash/debounce";

import { MeasurementSystem } from "src/js/stores/user/store";
import { AllGeoJSON } from "src/js/util/turf";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  convertUserUnitsToMeters,
  metersToWalkingMinutes,
  walkingMinutesToMeters,
} from "react-migration/lib/util/conversion";
import { radiusInUserUnits, unitOfMeasurement } from "react-migration/lib";

import { DrawingItem, DrawingsKeyboardEvent } from "../../../typings";

interface DrawingItemPoint {
  geometry: GeoJSON.Point;
}

export type RadiusInputProps = {
  updateGeometry: (scale: number, drawing: AllGeoJSON) => void;
  onChange?: (value: number) => void;
  drawing: DrawingItem | DrawingItemPoint | null | undefined;
  isSmallScale: boolean;
  unitPreference: MeasurementSystem;
  getRadius: (drawing: DrawingItem | DrawingItemPoint | null | undefined) => number;
};

export const RadiusInput: FC<RadiusInputProps> = ({
  updateGeometry,
  onChange,
  drawing,
  isSmallScale,
  unitPreference,
  getRadius,
}) => {
  const { t } = useTranslation();

  const onRadiusInputHandler = debounce((value, drawing) => {
    if (value && drawing) {
      updateGeometry(
        // use a rounded value to avoid floating point errors
        value /
          (Math.round(
            radiusInUserUnits({ radius: getRadius(drawing), unitPreference, isSmallScale }) * 100
          ) /
            100),
        drawing
      );
    }
    onChange?.(convertUserUnitsToMeters(value, { unitPreference, smallUnitSize: isSmallScale }));
  }, 100);

  const onWalkingInputHandler = debounce((value, drawing) => {
    const distance = walkingMinutesToMeters(value);
    if (value && drawing) {
      updateGeometry(distance / getRadius(drawing), drawing);
    }
    onChange?.(distance);
  }, 100);

  const handleOnKeyDown = (event: DrawingsKeyboardEvent) => {
    if (event.key === "Enter" && drawing && event.currentTarget.id === "walking-input") {
      onWalkingInputHandler(Number(event.currentTarget.value), drawing);
    }
    if (event.key === "Enter" && drawing && event.currentTarget.id === "radius-input") {
      onRadiusInputHandler(Number(event.currentTarget.value), drawing);
    }
  };

  return (
    <div
      className={"atlas-flex atlas-flex-row atlas-items-center"}
      data-testid="radius-input-wrapper"
    >
      <NumericInput
        className={"atlas-bg-content-inverse-primary  atlas-text-content-primary"}
        id="radius-input"
        data-testid="radius-input"
        isDisabled={!drawing}
        step={isSmallScale ? 1 : 0.1}
        onChange={(v) => onRadiusInputHandler(v, drawing)}
        value={
          Math.round(
            (radiusInUserUnits({ radius: getRadius(drawing), unitPreference, isSmallScale }) +
              Number.EPSILON) *
              (isSmallScale ? 1 : 100)
          ) / (isSmallScale ? 1 : 100)
        }
        units={unitOfMeasurement({ unitPreference, small: isSmallScale })}
        onKeyDown={handleOnKeyDown}
      />
      <span className={"atlas-mx-2 "} />
      <NumericInput
        className={"atlas-bg-content-inverse-primary  atlas-text-content-primary"}
        id="walking-input"
        data-testid="walking-input"
        isDisabled={!drawing}
        step={0.1}
        onChange={(v) => onWalkingInputHandler(v, drawing)}
        value={Number(metersToWalkingMinutes(getRadius(drawing)).toFixed(1))}
        units={t("comparables.calculator.drawingTools.walking.unit")}
        onKeyDown={handleOnKeyDown}
      />
    </div>
  );
};
